"use client";

import { cn } from "@/shared/lib/utils/cn";
import type { InsightsCoveragePlatform, InsightsCoverageSnapshot } from "./coverage";
import { InsightsToolkitLogo } from "./InsightsToolkitLogo";

function statusLabel(status: InsightsCoveragePlatform["status"]): string {
  switch (status) {
    case "live":
      return "Live";
    case "connected":
      return "Evaluating";
    case "thin":
      return "Thin";
    case "dead":
      return "Dead";
    case "blocked":
      return "Blocked";
    case "expired":
      return "Expired";
    case "missing":
      return "Missing";
    default:
      return "—";
  }
}

function statusClass(status: InsightsCoveragePlatform["status"]): string {
  switch (status) {
    case "live":
      return "border-emerald-500/30 bg-emerald-500/10 text-emerald-800 dark:text-emerald-300";
    case "connected":
      return "border-sky-500/30 bg-sky-500/10 text-sky-900 dark:text-sky-200";
    case "thin":
      return "border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200";
    case "blocked":
    case "expired":
    case "missing":
    case "dead":
      return "border-border bg-muted/60 text-muted-foreground";
    default:
      return "border-border/60 bg-muted/40 text-muted-foreground";
  }
}

/** Estate coverage chips — platforms actually used in this investigation. */
export function InsightsCoverageStrip({
  coverage,
  layout = "inline",
}: {
  coverage: InsightsCoverageSnapshot | null;
  /** `rail` matches investigation sidebar section headers. */
  layout?: "inline" | "rail";
}) {
  if (!coverage || coverage.platforms.length === 0) return null;

  if (layout === "rail") {
    return (
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-foreground">Coverage</h2>
        <div className="flex flex-wrap gap-1.5">
          {coverage.platforms.map((p) => (
            <span
              key={p.id}
              title={p.detail}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border py-0.5 pr-2 pl-1 text-[11px] font-medium",
                statusClass(p.status),
              )}
            >
              <span className="flex size-4 items-center justify-center rounded-full bg-background/80">
                <InsightsToolkitLogo system={p.id || p.label} className="size-3 object-contain" />
              </span>
              {p.label}
              <span className="opacity-70">· {statusLabel(p.status)}</span>
            </span>
          ))}
        </div>
      </section>
    );
  }

  return (
    <div className="mb-2 flex flex-wrap items-center gap-1.5 px-1">
      <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        Coverage
      </span>
      {coverage.platforms.map((p) => (
        <span
          key={p.id}
          title={p.detail}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border py-0.5 pr-2 pl-1 text-[11px] font-medium",
            statusClass(p.status),
          )}
        >
          <span className="flex size-4 items-center justify-center rounded-full bg-background/80">
            <InsightsToolkitLogo system={p.id || p.label} className="size-3 object-contain" />
          </span>
          {p.label}
          <span className="opacity-70">· {statusLabel(p.status)}</span>
        </span>
      ))}
    </div>
  );
}
